import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { DownloadOutlined } from "@ant-design/icons";
import { Button, Form, Popconfirm, Radio, Tooltip, Typography } from "antd";
import { EExtendsMergeStrategy, EUserRole, TemplateMetaDataField } from "@devowl-wp/api-real-cookie-banner";
import { yieldMainThread } from "@devowl-wp/react-utils";
import { useTranslation } from "../../../../others/i18n.js";
import { useStores } from "../../../../store/root.js";
const TemplateMetaDataExtendsStrategyFormField = ({ children, extendsFieldName, disableStrategies = [], defaultStrategy = EExtendsMergeStrategy.Keep, onKeepContent, allowCopyForOverride = true, onCopy = (getFieldValue, setFieldValue)=>{
    const extended = getFieldValue(TemplateMetaDataField.Extends);
    if (extended) {
        setFieldValue(extendsFieldName, structuredClone(extended[extendsFieldName]));
    }
} })=>{
    const { t } = useTranslation();
    const { rcbAuthenticationStore } = useStores();
    const { Text } = Typography;
    const isCreator = rcbAuthenticationStore.hasRole(EUserRole.Creator);
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: /*#__PURE__*/ _jsx(Form.Item, {
            shouldUpdate: (prev, next)=>prev.extends?.identifier !== next.extends?.identifier || prev.extendedMergeStrategies?.[extendsFieldName] !== next.extendedMergeStrategies?.[extendsFieldName],
            noStyle: true,
            children: ({ getFieldValue: getFieldValueStrict, setFieldValue: setFieldValueStrict })=>{
                const getFieldValue = getFieldValueStrict;
                const setFieldValue = setFieldValueStrict;
                const extended = getFieldValue(TemplateMetaDataField.Extends);
                const isExtended = !!extended;
                const extendsMergeStrategyPath = [
                    TemplateMetaDataField.ExtendedMergeStrategies,
                    extendsFieldName
                ];
                const currentMergeStrategy = getFieldValue(extendsMergeStrategyPath);
                if (!isExtended || !isCreator) {
                    return children;
                } else {
                    // If current merge strategy is not set or is disabled, set the default strategy. (CU-8695ekqxu)
                    if (!currentMergeStrategy || disableStrategies.includes(currentMergeStrategy)) {
                        yieldMainThread().then(()=>setFieldValue(extendsMergeStrategyPath, defaultStrategy));
                    }
                    return /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(Form.Item, {
                                noStyle: true,
                                name: extendsMergeStrategyPath,
                                children: /*#__PURE__*/ _jsxs(Radio.Group, {
                                    style: {
                                        marginBottom: "1rem"
                                    },
                                    size: "small",
                                    buttonStyle: "solid",
                                    children: [
                                        /*#__PURE__*/ _jsx(Radio.Button, {
                                            value: EExtendsMergeStrategy.Keep,
                                            disabled: disableStrategies.indexOf(EExtendsMergeStrategy.Keep) > -1,
                                            children: /*#__PURE__*/ _jsx(Tooltip, {
                                                title: t("Values from base template are kept."),
                                                children: t("Keep")
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx(Radio.Button, {
                                            value: EExtendsMergeStrategy.Append,
                                            disabled: disableStrategies.indexOf(EExtendsMergeStrategy.Append) > -1,
                                            children: /*#__PURE__*/ _jsx(Tooltip, {
                                                title: t("Values from base template are appended."),
                                                children: t("Append")
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx(Radio.Button, {
                                            value: EExtendsMergeStrategy.Overwrite,
                                            disabled: disableStrategies.indexOf(EExtendsMergeStrategy.Overwrite) > -1,
                                            children: /*#__PURE__*/ _jsx(Tooltip, {
                                                title: t("Values from base template are completely overwritten/replaced."),
                                                children: t("Overwrite")
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx(Radio.Button, {
                                            value: EExtendsMergeStrategy.Clear,
                                            disabled: disableStrategies.indexOf(EExtendsMergeStrategy.Clear) > -1,
                                            children: /*#__PURE__*/ _jsx(Tooltip, {
                                                title: t("No values from base template. This field remains void / clear."),
                                                children: t("Clear")
                                            })
                                        })
                                    ]
                                })
                            }),
                            /*#__PURE__*/ _jsx("br", {}),
                            EExtendsMergeStrategy.Clear === currentMergeStrategy && /*#__PURE__*/ _jsx("div", {
                                style: {
                                    display: "none"
                                },
                                children: children
                            }),
                            EExtendsMergeStrategy.Keep === currentMergeStrategy && onKeepContent,
                            [
                                EExtendsMergeStrategy.Overwrite,
                                EExtendsMergeStrategy.Append
                            ].includes(currentMergeStrategy) && /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        style: {
                                            opacity: 0.5
                                        },
                                        children: onKeepContent
                                    }),
                                    /*#__PURE__*/ _jsx("br", {}),
                                    /*#__PURE__*/ _jsxs(Text, {
                                        underline: true,
                                        children: [
                                            currentMergeStrategy === EExtendsMergeStrategy.Overwrite ? t("Replace") : t("Append"),
                                            ": "
                                        ]
                                    }),
                                    allowCopyForOverride && onCopy && currentMergeStrategy !== EExtendsMergeStrategy.Append && /*#__PURE__*/ _jsx(Tooltip, {
                                        title: t("Click to use values from base template for your adjustments."),
                                        children: /*#__PURE__*/ _jsxs(Popconfirm, {
                                            placement: "bottom",
                                            title: t("Overwrite current data?"),
                                            onConfirm: ()=>onCopy(getFieldValue, setFieldValue),
                                            children: [
                                                /*#__PURE__*/ _jsx(Button, {
                                                    icon: /*#__PURE__*/ _jsx(DownloadOutlined, {}),
                                                    type: "link",
                                                    size: "small",
                                                    children: t("Use Base")
                                                }),
                                                /*#__PURE__*/ _jsx("br", {})
                                            ]
                                        })
                                    }),
                                    children
                                ]
                            })
                        ]
                    });
                }
            }
        })
    });
};
export { TemplateMetaDataExtendsStrategyFormField };
